import React from 'react';
import { Link } from 'react-router-dom';
import {

    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Stack,
    Paper,
    IconButton,
    useTheme,
    alpha
} from '@mui/material';

import {
    Storage,
    Hub,
    Speed,
    Security,    
    DataObject,
    Memory,
    Architecture
} from '@mui/icons-material';

const VBase = () => {
    const theme = useTheme();

    const features = [
        {
            icon: <Hub />,
            title: 'Beehive Module',
            description: 'Store and retrieve unstructured data with lightning-fast vector search capabilities',
            color: '#FFA726'
        },
        {
            icon: <DataObject />,
            title: 'Nectar Engine',
            description: 'Optimized structured data storage with seamless querying and indexing',
            color: '#42A5F5'
        },
        {
            icon: <Speed />,
            title: 'Rapid Performance',
            description: 'Sub-millisecond query responses for AI workloads at global scale',
            color: '#66BB6A'
        },
        {
            icon: <Security />,
            title: 'Enterprise Security',
            description: 'Built-in encryption, compliance, and multi-tenant isolation',
            color: '#EF5350'
        }
    ];

    const useCases = [
        'Retrieval-Augmented Generation (RAG)',
        'Semantic Search',
        'Recommendation Systems',
        'Real-time Analytics',
        'AI Context Storage',
        'Document Embedding'
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    py: 8
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h2"
                                component="h1"
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                VBASE
                            </Typography>
                            <Typography variant="h5" color="text.secondary" gutterBottom>
                                Vector Database for Rapid AI
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                                Power your AI applications with VBASE's dual-engine architecture.
                                Beehive for unstructured vector data and Nectar for structured storage,
                                delivering unmatched performance for modern AI workloads.
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<Memory />}
                                    sx={{ px: 4 }}
                                >
                                    <Link to="https://github.com/BigFun123/vectorbase">Get Started</Link>
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<Architecture />}
                                >
                                    View Architecture
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={8}
                                sx={{
                                    p: 4,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                    <IconButton
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            width: 80,
                                            height: 80,
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        }}
                                    >
                                        <Storage sx={{ fontSize: 40 }} />
                                    </IconButton>
                                </Box>
                                <Typography variant="h6" align="center" gutterBottom>
                                    Dual-Engine Performance
                                </Typography>
                                <Typography variant="body2" align="center" color="text.secondary">
                                    Optimized for both vector similarity search and traditional structured queries
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h3" align="center" gutterBottom>
                    Core Modules
                </Typography>
                <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
                    Built for the demands of modern AI applications
                </Typography>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 8,
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box
                                            sx={{
                                                bgcolor: alpha(feature.color, 0.1),
                                                color: feature.color,
                                                p: 1.5,
                                                borderRadius: 2,
                                                mr: 2
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h5" component="h3">
                                            {feature.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Use Cases Section */}
            <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5), py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" gutterBottom>
                        AI Use Cases
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
                        Perfect for a wide range of AI and machine learning applications
                    </Typography>

                    <Grid container spacing={3} justifyContent="center">
                        {useCases.map((useCase, index) => (
                            <Grid item key={index}>
                                <Chip
                                    label={useCase}
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        py: 2,
                                        px: 1,
                                        fontSize: '1rem',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white'
                                        }
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Paper
                    elevation={4}
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: 'white'
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        Ready to Accelerate Your AI?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                        Deploy VBASE and experience the next generation of vector database performance
                    </Typography>                    
                </Paper>
            </Container>
        </Box>
    );
};

export default VBase;